// FULL CLEAN STABLE VERSION
import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { Trash2, Edit2, Heart, Send } from "lucide-react";
import { ConfirmModal } from "../../components/ConfirmModal";
import { MessageSquare } from "lucide-react";

/* ---------- TYPES ---------- */

interface Media {
  id: string;
  file_url: string;
  type: "image" | "video";
}

interface Comment {
  id: string;
  post_id: string;
  content: string;
  parent_id: string | null;
  created_at: string;
  author?: {
    first_name: string | null;
    last_name: string | null;
  } | null;
  replies?: Comment[];
}

interface Post {
  id: string;
  content: string;
  created_at: string;
  media: Media[];
  likes: number;
  likedByMe: boolean;
  currentIndex: number;
  author?: {
    first_name: string | null;
    last_name: string | null;
  } | null;
  comments: Comment[];
}

/* ---------- COMPONENT ---------- */

export function AdminFeedTab() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [newContent, setNewContent] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [editFiles, setEditFiles] = useState<File[]>([]);
  const [mediaToDelete, setMediaToDelete] = useState<string[]>([]);

  const [commentInput, setCommentInput] = useState<Record<string, string>>({});
  const [replyInput, setReplyInput] = useState<Record<string, string>>({});
  const [showReply, setShowReply] = useState<Record<string, boolean>>({});

  const [deleteId, setDeleteId] = useState<string | null>(null);

  /* ---------- INIT ---------- */

  useEffect(() => {
    loadProfile();
    loadPosts();
  }, []);

  async function loadProfile() {
    const { data } = await supabase.auth.getUser();
    const userId = data.user?.id;
    if (!userId) return;

    const { data: p } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    setProfile(p);
  }

  function canManage() {
    if (!profile) return false;
    if (profile.role === "superadmin") return true;
    if (profile.role === "admin" && profile.admin_level >= 3) return true;
    return false;
  }

  /* ---------- LOAD POSTS ---------- */

  async function loadPosts() {
    setLoading(true);

    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;

    const { data } = await supabase
      .from("posts")
      .select(`
        *,
        profiles:author_id(first_name,last_name),
        post_media(*),
        post_likes(user_id),
        post_comments(
          *,
          profiles:user_id(first_name,last_name)
        )
      `)
      .order("created_at", { ascending: false });

    if (!data) {
      setLoading(false);
      return;
    }

    const formatted = data.map((p: any) => ({
      id: p.id,
      content: p.content,
      created_at: p.created_at,
      media: p.post_media || [],
      likes: p.post_likes?.length || 0,
      likedByMe: p.post_likes?.some((l: any) => l.user_id === userId),
      currentIndex: 0,
      author: p.profiles || null,
      comments: (p.post_comments || []).map((c: any) => ({
        ...c,
        author: c.profiles || null,
      })),
    }));

    setPosts(formatted);
    setLoading(false);
  }

  /* ---------- CREATE POST ---------- */

  async function createPost() {
    if (!newContent.trim() && files.length === 0) return;
    if (!canManage()) return;

    const { data } = await supabase.auth.getUser();
    const userId = data.user?.id;

    const { data: post } = await supabase
      .from("posts")
      .insert({ content: newContent, author_id: userId })
      .select()
      .single();

    if (!post) return;

    for (const file of files) {
      const path = `${post.id}/${Date.now()}-${file.name}`;

      const { error } = await supabase.storage
        .from("community")
        .upload(path, file);

      if (error) {
        console.error("Upload error:", error);
        continue;
      }

      const { data: publicData } = supabase.storage
        .from("community")
        .getPublicUrl(path);

      await supabase.from("post_media").insert({
        post_id: post.id,
        file_url: publicData.publicUrl,
        type: file.type.startsWith("video") ? "video" : "image",
      });
    }

    setNewContent("");
    setFiles([]);
    loadPosts();
  }

  /* ---------- DELETE ---------- */

  async function confirmDelete() {
    if (!deleteId) return;

    // delete storage files
    const { data: media } = await supabase
      .from("post_media")
      .select("file_url")
      .eq("post_id", deleteId);

    if (media) {
      for (const m of media) {
        const path = m.file_url.split("/community/")[1];
        if (path) {
          await supabase.storage.from("community").remove([path]);
        }
      }
    }

    await supabase.from("posts").delete().eq("id", deleteId);
    setDeleteId(null);
    loadPosts();
  }

  /* ---------- LIKE ---------- */

  async function toggleLike(postId: string, liked: boolean) {
    const { data } = await supabase.auth.getUser();
    const userId = data.user?.id;
    if (!userId) return;

    if (liked) {
      await supabase.from("post_likes").delete().eq("post_id", postId).eq("user_id", userId);
    } else {
      await supabase.from("post_likes").insert({ post_id: postId, user_id: userId });
    }

    setPosts(prev =>
      prev.map(p =>
        p.id === postId
          ? { ...p, likedByMe: !liked, likes: liked ? p.likes - 1 : p.likes + 1 }
          : p
      )
    );
  }

  /* ---------- COMMENTS ---------- */

  function buildTree(comments: Comment[]): Comment[] {
    const sorted = [...comments].sort(
      (a, b) =>
        new Date(b.created_at).getTime() -
        new Date(a.created_at).getTime()
    );

    const map = new Map<string, Comment & { replies: Comment[] }>();
    sorted.forEach(c => map.set(c.id, { ...c, replies: [] }));

    const tree: Comment[] = [];
    map.forEach(c => {
      if (c.parent_id && map.has(c.parent_id)) {
        map.get(c.parent_id)!.replies.push(c);
      } else {
        tree.push(c);
      }
    });

    return tree;
  }

  async function addComment(postId: string, parentId?: string) {
  const content = parentId ? replyInput[parentId] : commentInput[postId];
  if (!content?.trim()) return;

  const { data: auth } = await supabase.auth.getUser();
  const userId = auth.user?.id;
  if (!userId) return;

  const { data: profileData } = await supabase
    .from("profiles")
    .select("first_name,last_name")
    .eq("id", userId)
    .single();

  const { data: inserted } = await supabase
    .from("post_comments")
    .insert({
      post_id: postId,
      user_id: userId,
      content,
      parent_id: parentId || null,
    })
    .select()
    .single();

  if (!inserted) return;

  const newComment = {
    ...inserted,
    author: profileData || null,
    replies: [],
  };

  setPosts(prev =>
    prev.map(post => {
      if (post.id !== postId) return post;

      return {
        ...post,
        comments: [...post.comments, newComment],
      };
    })
  );

  setCommentInput(prev => ({ ...prev, [postId]: "" }));
  setReplyInput(prev => ({ ...prev, [parentId || ""]: "" }));
}

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="space-y-6 p-6">

      {canManage() && (
  <div className="bg-white p-4 rounded-xl shadow space-y-4">

    <textarea
      className="w-full border p-2 rounded"
      placeholder="Write something..."
      value={newContent}
      onChange={e => setNewContent(e.target.value)}
    />

    <input
  type="file"
  multiple
  onChange={e => {
    if (!e.target.files) return;

    const selected = Array.from(e.target.files);

    // APPEND instead of replace
    setFiles(prev => [...prev, ...selected]);

    // reset so same file can be reselected
    e.target.value = "";
  }}
/>

    {/* PREVIEW */}
    {files.length > 0 && (
      <div className="flex flex-wrap gap-3">
        {files.map((file, index) => {
          const preview = URL.createObjectURL(file);
          return (
            <div key={index} className="relative">
              {file.type.startsWith("image") ? (
                <img
                  src={preview}
                  className="h-24 w-24 object-cover rounded-lg"
                />
              ) : (
                <video
                  src={preview}
                  className="h-24 w-24 object-cover rounded-lg"
                />
              )}

              <button
                onClick={() =>
                  setFiles(prev => prev.filter((_, i) => i !== index))
                }
                className="absolute -top-2 -right-2 bg-red-600 text-white w-5 h-5 text-xs rounded-full"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    )}

    <button
      onClick={createPost}
      className="bg-nhs-blue text-white px-4 py-2 rounded"
    >
      Post
    </button>

  </div>
)}

      {posts.map(post => (
        <div key={post.id} className="bg-white rounded-xl shadow p-4 space-y-4">

          <div className="flex justify-between">
            <div>
              <div className="font-semibold">
                {post.author?.first_name} {post.author?.last_name}
              </div>
              <div className="text-xs text-gray-500">
                {new Date(post.created_at).toLocaleString("en-GB")}
              </div>
            </div>

            {canManage() && (
              <div className="flex gap-3">
                <button onClick={() => { 
                  setEditingId(post.id); 
                  setEditContent(post.content); 
                  setEditFiles([]);
                  setMediaToDelete([]);
                }}>
                  <Edit2 size={18} />
                </button>
                <button onClick={() => setDeleteId(post.id)}>
                  <Trash2 size={18} className="text-red-600" />
                </button>
              </div>
            )}
          </div>

          {editingId === post.id ? (
  <>
    <textarea
      className="w-full border p-2 rounded"
      value={editContent}
      onChange={e => setEditContent(e.target.value)}
    />

    {/* EXISTING MEDIA */}
    {post.media.length > 0 && (
      <div className="flex flex-wrap gap-3 mt-3">
        {post.media.map(m => (
          <div key={m.id} className="relative">
            {m.type === "image" ? (
              <img
                src={m.file_url}
                className="h-24 w-24 object-cover rounded-lg"
              />
            ) : (
              <video
                src={m.file_url}
                className="h-24 w-24 object-cover rounded-lg"
              />
            )}

            <button
              onClick={() =>
                setMediaToDelete(prev => [...prev, m.id])
              }
              className="absolute -top-2 -right-2 bg-red-600 text-white w-5 h-5 text-xs rounded-full"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    )}

    {/* ADD NEW MEDIA */}
    <input
      type="file"
      multiple
      onChange={e => {
        if (!e.target.files) return;
        setEditFiles(prev => [...prev, ...Array.from(e.target.files)]);
        e.target.value = "";
      }}
    />

    {/* NEW MEDIA PREVIEW */}
    {editFiles.length > 0 && (
      <div className="flex flex-wrap gap-3 mt-3">
        {editFiles.map((file, index) => {
          const preview = URL.createObjectURL(file);
          return (
            <div key={index} className="relative">
              {file.type.startsWith("image") ? (
                <img
                  src={preview}
                  className="h-24 w-24 object-cover rounded-lg"
                />
              ) : (
                <video
                  src={preview}
                  className="h-24 w-24 object-cover rounded-lg"
                />
              )}
              <button
                onClick={() =>
                  setEditFiles(prev =>
                    prev.filter((_, i) => i !== index)
                  )
                }
                className="absolute -top-2 -right-2 bg-red-600 text-white w-5 h-5 text-xs rounded-full"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    )}

    <button
      onClick={async () => {
        // UPDATE TEXT
        await supabase
          .from("posts")
          .update({ content: editContent })
          .eq("id", post.id);

        // DELETE SELECTED MEDIA
        if (mediaToDelete.length > 0) {
          const { data } = await supabase
            .from("post_media")
            .select("file_url")
            .in("id", mediaToDelete);

          if (data) {
            for (const m of data) {
              const path = m.file_url.split("/community/")[1];
              if (path) {
                await supabase.storage.from("community").remove([path]);
              }
            }
          }

          await supabase
            .from("post_media")
            .delete()
            .in("id", mediaToDelete);
        }

        // ADD NEW MEDIA
        for (const file of editFiles) {
          const path = `${post.id}/${Date.now()}-${file.name}`;

          await supabase.storage
            .from("community")
            .upload(path, file);

          const { data } = supabase.storage
            .from("community")
            .getPublicUrl(path);

          await supabase.from("post_media").insert({
            post_id: post.id,
            file_url: data.publicUrl,
            type: file.type.startsWith("video") ? "video" : "image",
          });
        }

        setEditingId(null);
        setEditFiles([]);
        setMediaToDelete([]);
        loadPosts();
      }}
      className="bg-green-600 text-white px-3 py-1 rounded mt-3"
    >
      Save
    </button>
  </>
) : (
  <div>{post.content}</div>
)}

          {/* MEDIA DISPLAY */}
          {post.media.length > 0 && (
            <div className="relative flex justify-center">
              {post.media[post.currentIndex]?.type === "image" ? (
                <img
                  src={post.media[post.currentIndex].file_url}
                  className="max-h-[500px] object-contain rounded-lg"
                />
              ) : (
                <video
                  src={post.media[post.currentIndex].file_url}
                  controls
                  className="max-h-[500px] object-contain rounded-lg"
                />
              )}

              {post.media.length > 1 && (
                <>
                  <button
                    onClick={() =>
                      setPosts(prev =>
                        prev.map(p =>
                          p.id === post.id
                            ? {
                                ...p,
                                currentIndex:
                                  p.currentIndex === 0
                                    ? p.media.length - 1
                                    : p.currentIndex - 1,
                              }
                            : p
                        )
                      )
                    }
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 text-white w-8 h-8 rounded-full"
                  >
                    ‹
                  </button>

                  <button
                    onClick={() =>
                      setPosts(prev =>
                        prev.map(p =>
                          p.id === post.id
                            ? {
                                ...p,
                                currentIndex:
                                  p.currentIndex === p.media.length - 1
                                    ? 0
                                    : p.currentIndex + 1,
                              }
                            : p
                        )
                      )
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 text-white w-8 h-8 rounded-full"
                  >
                    ›
                  </button>
                </>
              )}
            </div>
          )}

          <div className="flex items-center gap-6">
            <button
              onClick={() => toggleLike(post.id, post.likedByMe)}
              className={`flex items-center gap-2 ${
                post.likedByMe ? "text-blue-600" : "text-gray-600"
              }`}
            >
              <Heart className={`h-5 w-5 ${post.likedByMe ? "fill-blue-600" : ""}`} />
              {post.likes}
            </button>

            <div
  className="flex items-center gap-1 text-gray-600"
  title={`${post.comments.length} comment${post.comments.length !== 1 ? "s" : ""}`}
>
  <MessageSquare className="h-4 w-4 opacity-70" />
  {post.comments.length}
</div>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={commentInput[post.id] || ""}
              onChange={e =>
                setCommentInput(prev => ({ ...prev, [post.id]: e.target.value }))
              }
              placeholder="Write a comment..."
              className="flex-1 border rounded px-3 py-2 text-sm"
            />
            <button
              onClick={() => addComment(post.id)}
              className="bg-blue-600 text-white px-3 py-2 rounded"
            >
              <Send size={16} />
            </button>
          </div>

          {buildTree(post.comments).map(comment => (
            <CommentTree
              key={comment.id}
              comment={comment}
              postId={post.id}
              replyInput={replyInput}
              setReplyInput={setReplyInput}
              addComment={addComment}
            />
          ))}
        </div>
      ))}

      <ConfirmModal
        open={!!deleteId}
        title="Delete Post"
        message="Are you sure you want to delete this post?"
        variant="danger"
        confirmText="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}

/* ---------- RECURSIVE COMMENT ---------- */

function CommentTree({ comment, postId, replyInput, setReplyInput, addComment }: any) {
  const [show, setShow] = useState(false);

  return (
    <div className="mt-3 border-t pt-3 text-sm">
      <div className="font-medium">
        {comment.author?.first_name} {comment.author?.last_name}
      </div>
      <div>{comment.content}</div>

      <button onClick={() => setShow(!show)} className="text-xs text-blue-600 mt-1">
        Reply
      </button>

      {show && (
        <div className="flex gap-2 mt-2 ml-4">
          <input
            type="text"
            value={replyInput[comment.id] || ""}
            onChange={e =>
              setReplyInput((prev: any) => ({
                ...prev,
                [comment.id]: e.target.value,
              }))
            }
            className="flex-1 border rounded px-2 py-1 text-sm"
          />
          <button
            onClick={() => addComment(postId, comment.id)}
            className="bg-blue-600 text-white px-2 py-1 rounded text-xs"
          >
            Send
          </button>
        </div>
      )}

      {comment.replies?.map((r: any) => (
        <div key={r.id} className="ml-6">
          <CommentTree
            comment={r}
            postId={postId}
            replyInput={replyInput}
            setReplyInput={setReplyInput}
            addComment={addComment}
          />
        </div>
      ))}
    </div>
  );
}